import React, { useState, useEffect, useRef, useMemo } from 'react';
import { productAPI, orderAPI, dealerAPI, analyticsAPI, exportAPI, transporterAPI, crmAPI, stockAPI, productionAPI, getFileUrl } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingBag, Users, LogOut, Plus, Edit, Trash2, CheckCircle, Truck, Loader2, FileText, BarChart3, PackageCheck, DollarSign, Download, IndianRupee, Upload, ImageIcon, ArrowUpDown, Eye, CalendarClock, Pencil, ClipboardList, Undo2, Menu, X, UserRound, MessageCircle, ArrowLeft, Warehouse, Minus, Factory } from 'lucide-react';
import { FACILITIES_CLIENT } from './constants';
import { useConfirm } from '@/components/ConfirmProvider';

// ============ DELIVERY VIEW (inside Stock tab) ============
export const DeliveryView = ({ products = [], orders = [], transporters = [], userRole, onChanged, refreshKey = 0 }) => {
  // Manager role is read-only for stock corrections; only admins can hard-
  // delete a stray in-transit dispatch. Default false → fail closed if the
  // prop isn't wired.
  const isAdmin = (userRole || '').toLowerCase() === 'admin';
  const confirm = useConfirm();
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDelivered, setShowDelivered] = useState(true);
  const [markingId, setMarkingId] = useState(null);
  const [bulkMarking, setBulkMarking] = useState(null); // { done, total } while bulk mark running
  // Delivery split dialog
  const [splitDlg, setSplitDlg] = useState(null); // { row }
  const [splitQty, setSplitQty] = useState({});   // { product_id: {E-302, E-303} }
  const [expandedProducts, setExpandedProducts] = useState({}); // { product_id: bool }
  const toggleProduct = (pid) => setExpandedProducts(prev => ({ ...prev, [pid]: !prev[pid] }));
  // Date filter for delivered stack — default to today
  const todayISO = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local timezone
  const [stackDateFrom, setStackDateFrom] = useState(todayISO);
  const [stackDateTo, setStackDateTo] = useState(todayISO);
  const setStackDatePreset = (preset) => {
    const now = new Date();
    const fmt = (d) => d.toLocaleDateString('en-CA');
    if (preset === 'today') { setStackDateFrom(fmt(now)); setStackDateTo(fmt(now)); }
    else if (preset === 'yesterday') {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      setStackDateFrom(fmt(y)); setStackDateTo(fmt(y));
    }
    else if (preset === 'week') {
      const w = new Date(now); w.setDate(w.getDate() - 6);
      setStackDateFrom(fmt(w)); setStackDateTo(fmt(now));
    }
    else if (preset === 'month') {
      const m = new Date(now.getFullYear(), now.getMonth(), 1);
      setStackDateFrom(fmt(m)); setStackDateTo(fmt(now));
    }
    else if (preset === 'all') { setStackDateFrom(''); setStackDateTo(''); }
  };
  // Dealer filter for delivered stack — exact-name match against the
  // dropdown selection (still substring-compatible with backend export).
  const [stackDealerQuery, setStackDealerQuery] = useState('');
  // Unique alphabetically-sorted list of dealer names that appear on any
  // DELIVERED dispatch — this powers the dealer dropdown in the delivered
  // stack filter bar. Rebuilt whenever `dispatches` changes.
  const stackDealerOptions = React.useMemo(() => {
    const names = new Set();
    (dispatches || []).forEach(d => {
      if (d.delivered_at && d.dealer_name) names.add(d.dealer_name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [dispatches]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await orderAPI.listDispatches({ status: showDelivered ? 'all' : 'active' });
      setDispatches(res.data);
    } catch (e) {
      toast.error('Failed to load dispatches');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showDelivered]);

  // Silent refresh on parent-driven re-entry. Skips the first render — the
  // [showDelivered] effect above already covers initial mount. Local UI state
  // (expandedProducts, stack date filter, dealer query, scroll position) is
  // preserved because we only update the `dispatches` array via setDispatches.
  const isFirstDeliveryRefreshRef = React.useRef(true);
  useEffect(() => {
    if (isFirstDeliveryRefreshRef.current) { isFirstDeliveryRefreshRef.current = false; return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const fmtDateTime = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return '-'; }
  };

  const openSplitDlg = (row) => {
    setSplitDlg({ row });
    // Default: whole qty from E-303, 0 from E-302 (one-click flow for single-facility)
    const init = {};
    (row.items || []).forEach(it => { init[it.product_id] = { 'E-302': 0, 'E-303': it.quantity }; });
    setSplitQty(init);
  };

  const updateSplit = (productId, facility, value, totalQty) => {
    const v = Math.max(0, Math.min(totalQty, parseInt(value || 0, 10) || 0));
    const other = facility === 'E-302' ? 'E-303' : 'E-302';
    setSplitQty(prev => ({
      ...prev,
      [productId]: { [facility]: v, [other]: Math.max(0, totalQty - v) },
    }));
  };

  const setAllToFacility = (facility) => {
    if (!splitDlg?.row) return;
    const next = {};
    const other = facility === 'E-302' ? 'E-303' : 'E-302';
    (splitDlg.row.items || []).forEach(it => {
      next[it.product_id] = { [facility]: it.quantity, [other]: 0 };
    });
    setSplitQty(next);
  };

  const confirmDelivery = async () => {
    if (!splitDlg?.row) return;
    const row = splitDlg.row;
    // Validate per-product totals and build the split payload
    const payload = [];
    for (const it of (row.items || [])) {
      const e302 = parseInt(splitQty[it.product_id]?.['E-302'] || 0, 10) || 0;
      const e303 = parseInt(splitQty[it.product_id]?.['E-303'] || 0, 10) || 0;
      if (e302 + e303 !== it.quantity) {
        toast.error(`${it.product_name}: split must total ${it.quantity} (currently ${e302 + e303})`);
        return;
      }
      if (e302 > 0) payload.push({ facility: 'E-302', product_id: it.product_id, quantity: e302 });
      if (e303 > 0) payload.push({ facility: 'E-303', product_id: it.product_id, quantity: e303 });
    }
    if (payload.length === 0) {
      toast.error('At least one product must have quantity > 0');
      return;
    }
    setMarkingId(row.dispatch_id);
    try {
      const res = await orderAPI.markDelivered(row.order_id, row.dispatch_id, { splits: payload });
      const facsUsed = [...new Set(payload.map(p => p.facility))];
      const facLabel = facsUsed.length === 1 ? `from ${facsUsed[0]}` : `split across ${facsUsed.join(' + ')}`;
      toast.success(res.data.order_status === 'delivered' ? `Delivered ${facLabel} \u2014 order closed` : `Delivered ${facLabel}`);
      setSplitDlg(null);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark delivered');
    } finally { setMarkingId(null); }
  };

  const markAllInTransitDeliveredToday = async () => {
    const live = dispatches.filter(d => !d.delivered_at);
    if (live.length === 0) {
      toast.error('No in-transit dispatches to mark');
      return;
    }
    const ok = await confirm({
      title: `Mark all ${live.length} in-transit as delivered today?`,
      description: 'Facility-less dispatches default to E-303. This cannot be undone in bulk (each must be reverted individually).',
      confirmLabel: `Mark ${live.length} delivered`,
    });
    if (!ok) return;

    setBulkMarking({ done: 0, total: live.length });
    let ok_n = 0;
    let fail_n = 0;
    for (let i = 0; i < live.length; i++) {
      const row = live[i];
      try {
        if (row.facility) {
          await orderAPI.markDelivered(row.order_id, row.dispatch_id);
        } else {
          const splits = [];
          for (const it of (row.items || [])) {
            if (Number(it.quantity) > 0) splits.push({ facility: 'E-303', product_id: it.product_id, quantity: Number(it.quantity) });
          }
          await orderAPI.markDelivered(row.order_id, row.dispatch_id, { splits });
        }
        ok_n += 1;
      } catch (e) {
        fail_n += 1;
      }
      setBulkMarking({ done: i + 1, total: live.length });
    }
    setBulkMarking(null);
    if (fail_n === 0) toast.success(`All ${ok_n} dispatches marked delivered`);
    else toast.error(`${ok_n} delivered · ${fail_n} failed`);
    await load();
    onChanged && onChanged();
  };

  const markDelivered = async (row) => {
    // If dispatch is facility-less, open the split dialog. Otherwise just confirm.
    if (!row.facility) {
      openSplitDlg(row);
      return;
    }
    if (!(await confirm({
      title: `Mark ${row.facility} dispatch as delivered?`,
      description: `Confirm delivery to ${row.dealer_name}.`,
      confirmLabel: 'Mark delivered',
    }))) return;
    setMarkingId(row.dispatch_id);
    try {
      const res = await orderAPI.markDelivered(row.order_id, row.dispatch_id);
      toast.success(res.data.order_status === 'delivered' ? 'Delivered \u2014 order closed' : 'Dispatch marked delivered');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark delivered');
    } finally { setMarkingId(null); }
  };

  // Grouping: all UNDELIVERED dispatches in one "In Transit" section.
  // Delivered dispatches are aggregated below into the Product × Facility stack view.
  const inTransit = dispatches.filter(d => !d.delivered_at);

  const revertDelivery = async (row) => {
    if (!(await confirm({
      title: 'Revert delivery back to In Transit?',
      description: 'Stock will be added back to the original facilities.',
      confirmLabel: 'Revert',
      destructive: true,
    }))) return;
    setMarkingId(row.dispatch_id);
    try {
      await orderAPI.revertDelivery(row.order_id, row.dispatch_id);
      toast.success('Delivery reverted — dispatch is back in transit');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revert');
    } finally { setMarkingId(null); }
  };

  const deleteDispatch = async (row) => {
    if (!(await confirm({
      title: 'Delete this in-transit dispatch?',
      description: `This permanently removes the dispatch (vehicle ${row.vehicle_number || '—'}, ${(row.items || []).map(i => `${i.product_name}×${i.quantity}`).join(', ')}). Any facility-attributed stock is added back. Use this only to clean up duplicates created by double-submit.`,
      confirmLabel: 'Delete dispatch',
      destructive: true,
    }))) return;
    setMarkingId(row.dispatch_id);
    try {
      await orderAPI.deleteDispatch(row.order_id, row.dispatch_id);
      toast.success('Dispatch deleted — stock returned to facility');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete dispatch');
    } finally { setMarkingId(null); }
  };

  const renderRow = (r, opts = {}) => {
    const isDelivered = !!r.delivered_at;
    return (
      <tr key={r.dispatch_id + (opts.facility || '')} className="border-t" style={{ borderColor: '#E2DFD8' }} data-testid="dispatch-row">
        <td className="p-2">{fmtDateTime(r.dispatched_at)}<div className="text-[10px]" style={{ color: '#5C6B61' }}>by {r.created_by}</div></td>
        <td className="p-2">{r.dealer_name}</td>
        <td className="p-2">{(r.items || []).map(i => `${i.product_name}\u00D7${i.quantity}`).join(', ')}</td>
        <td className="p-2">{r.vehicle_number || <span style={{ color: '#C05C35' }}>—</span>}</td>
        <td className="p-2">{r.driver_mobile || <span style={{ color: '#C05C35' }}>—</span>}</td>
        <td className="p-2">
          {isDelivered ? (
            <span className="text-[10px] font-semibold" style={{ color: '#2B5339' }}>DELIVERED {fmtDateTime(r.delivered_at)}</span>
          ) : (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: '#E89D4F', color: 'white' }}>IN TRANSIT</span>
          )}
        </td>
        <td className="p-2 text-center">
          {!isDelivered ? (
            <div className="flex flex-col items-center gap-1">
              <Button size="sm" className="h-7 text-xs" style={{ background: '#2B5339' }} onClick={() => markDelivered(r)} disabled={markingId === r.dispatch_id} data-testid="mark-delivered-btn">
                {markingId === r.dispatch_id ? <Loader2 className="w-3 h-3 animate-spin" /> : (<><PackageCheck className="w-3 h-3 mr-1" />Delivered</>)}
              </Button>
              {/* Delete stray duplicate — admin-only since the action is
                  destructive (used to clean up double-submit duplicates).
                  Managers see Mark-Delivered only. */}
              {isAdmin && (
                <button
                  onClick={() => deleteDispatch(r)}
                  disabled={markingId === r.dispatch_id}
                  title="Delete this stray dispatch (use only for double-submit duplicates)"
                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border rounded hover:bg-[#FBF9F6]"
                  style={{ borderColor: '#C05C35', color: '#C05C35' }}
                  data-testid="delete-dispatch-btn"
                >
                  Delete
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <span className="text-[10px]" style={{ color: '#5C6B61' }}>by {r.delivered_by || '-'}</span>
              <button onClick={() => revertDelivery(r)} disabled={markingId === r.dispatch_id} title="Revert to In Transit" className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border rounded hover:bg-[#FBF9F6]" style={{ borderColor: '#C05C35', color: '#C05C35' }} data-testid="revert-delivery-btn">
                <Undo2 className="w-3 h-3" /> Revert
              </button>
            </div>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div data-testid="delivery-view">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm" style={{ color: '#5C6B61' }}>
          <b>In Transit</b> = dispatched but goods still in godown · <b>Delivered</b> = goods physically out of godown (irrespective of buyer receipt). Click <b>Delivered</b> to pick the source facility.
        </div>
        <label className="inline-flex items-center gap-1 text-xs cursor-pointer" data-testid="show-delivered-toggle">
          <input type="checkbox" checked={showDelivered} onChange={(e) => setShowDelivered(e.target.checked)} /> Include delivered
        </label>
      </div>

      {/* In Transit (all undelivered dispatches) */}
      <Card className="mb-5" data-testid="delivery-in-transit">
        <div className="p-3 border-b flex items-center justify-between gap-2 flex-wrap" style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }}>
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4" style={{ color: '#E89D4F' }} />
            <div className="font-bold" style={{ color: '#2B5339' }}>In Transit</div>
            <span className="text-[11px]" style={{ color: '#5C6B61' }}>{inTransit.length} live</span>
          </div>
          {inTransit.length > 0 && (
            <Button
              size="sm"
              className="h-7 text-xs"
              style={{ background: '#2B5339' }}
              onClick={markAllInTransitDeliveredToday}
              disabled={!!bulkMarking}
              data-testid="mark-all-delivered-today-btn"
              title="Mark every in-transit dispatch as delivered today"
            >
              {bulkMarking ? (
                <><Loader2 className="w-3 h-3 animate-spin mr-1" /> {bulkMarking.done}/{bulkMarking.total}</>
              ) : (
                <><PackageCheck className="w-3 h-3 mr-1" /> Mark all {inTransit.length} delivered today</>
              )}
            </Button>
          )}
        </div>
        <div className="overflow-auto">
          <table className="w-full text-xs">
            <thead style={{ background: 'white' }}>
              <tr>
                <th className="p-2 text-left">Dispatched</th>
                <th className="p-2 text-left">Dealer</th>
                <th className="p-2 text-left">Items</th>
                <th className="p-2 text-left">Vehicle</th>
                <th className="p-2 text-left">Driver</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="p-6 text-center"><Loader2 className="inline w-4 h-4 animate-spin" /></td></tr>
              ) : inTransit.length === 0 ? (
                <tr><td colSpan="7" className="p-6 text-center" style={{ color: '#5C6B61' }}>Nothing in transit.</td></tr>
              ) : inTransit.map(r => renderRow(r))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Delivered Stack — Product (rows) × Facility (columns), collapsible */}
      {showDelivered && (() => {
        // Aggregate: product_id -> { product_name, by_facility, unattributed, dispatches[] (with _per_facility, _unattributed) }
        const stack = {};
        let anyUnattributed = false;
        // Apply date filter on delivered_at (IST-calendar-day string comparison,
        // matches backend `_build_delivered_stack` in exports.py). This is
        // timezone-safe: it converts each dispatch's UTC `delivered_at` to its
        // IST calendar day, then compares YYYY-MM-DD strings against the
        // YYYY-MM-DD values from the date inputs (which are already in the
        // user's local time zone — IST for our operators).
        // If the operator picks From > To (e.g., changes only From and forgets
        // to bump To), we auto-swap the bounds so the range is always valid.
        let fromStr = (stackDateFrom || '').trim();
        let toStr = (stackDateTo || '').trim();
        if (fromStr && toStr && fromStr > toStr) {
          const tmp = fromStr; fromStr = toStr; toStr = tmp;
        }
        const istDay = (iso) => {
          try {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return null;
            return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
          } catch { return null; }
        };
        const inRange = (iso) => {
          if (!iso) return false;
          const day = istDay(iso);
          if (!day) return false;
          if (fromStr && day < fromStr) return false;
          if (toStr && day > toStr) return false;
          return true;
        };
        for (const d of dispatches.filter(x => !!x.delivered_at && inRange(x.delivered_at) && (
          !stackDealerQuery ||
          (x.dealer_name || '').toLowerCase().includes(stackDealerQuery.toLowerCase()) ||
          (x.dealer_phone || '').toLowerCase().includes(stackDealerQuery.toLowerCase())
        ))) {
          const splits = d.delivery_splits || [];
          if (splits.length > 0) {
            // Group splits by product_id within this dispatch
            const byProduct = {};
            for (const s of splits) {
              if (!FACILITIES_CLIENT.includes(s.facility)) continue;
              byProduct[s.product_id] = byProduct[s.product_id] || Object.fromEntries(FACILITIES_CLIENT.map(f => [f, 0]));
              byProduct[s.product_id][s.facility] += Number(s.quantity || 0);
            }
            for (const [pid, perFac] of Object.entries(byProduct)) {
              const oi = (d.items || []).find(i => i.product_id === pid) || {};
              const cell = stack[pid] = stack[pid] || {
                product_name: oi.product_name || pid,
                by_facility: Object.fromEntries(FACILITIES_CLIENT.map(f => [f, 0])),
                unattributed: 0,
                dispatches: [],
              };
              FACILITIES_CLIENT.forEach(f => { cell.by_facility[f] += perFac[f] || 0; });
              cell.dispatches.push({ ...d, _per_facility: perFac, _unattributed: 0 });
            }
          } else if (d.facility && FACILITIES_CLIENT.includes(d.facility)) {
            // Legacy: facility-attributed at dispatch time
            for (const it of (d.items || [])) {
              const cell = stack[it.product_id] = stack[it.product_id] || {
                product_name: it.product_name,
                by_facility: Object.fromEntries(FACILITIES_CLIENT.map(f => [f, 0])),
                unattributed: 0,
                dispatches: [],
              };
              cell.by_facility[d.facility] += Number(it.quantity || 0);
              const existing = cell.dispatches.find(x => x.dispatch_id === d.dispatch_id);
              if (existing) {
                existing._per_facility[d.facility] = (existing._per_facility[d.facility] || 0) + Number(it.quantity || 0);
              } else {
                const perFac = Object.fromEntries(FACILITIES_CLIENT.map(f => [f, 0]));
                perFac[d.facility] = Number(it.quantity || 0);
                cell.dispatches.push({ ...d, _per_facility: perFac, _unattributed: 0 });
              }
            }
          } else {
            // Unattributed: legacy delivered dispatch with no facility/splits — keep visible
            anyUnattributed = true;
            for (const it of (d.items || [])) {
              const cell = stack[it.product_id] = stack[it.product_id] || {
                product_name: it.product_name,
                by_facility: Object.fromEntries(FACILITIES_CLIENT.map(f => [f, 0])),
                unattributed: 0,
                dispatches: [],
              };
              cell.unattributed += Number(it.quantity || 0);
              const existing = cell.dispatches.find(x => x.dispatch_id === d.dispatch_id);
              if (existing) {
                existing._unattributed = (existing._unattributed || 0) + Number(it.quantity || 0);
              } else {
                cell.dispatches.push({
                  ...d,
                  _per_facility: Object.fromEntries(FACILITIES_CLIENT.map(f => [f, 0])),
                  _unattributed: Number(it.quantity || 0),
                });
              }
            }
          }
        }
        const products = Object.entries(stack).map(([pid, c]) => ({
          product_id: pid,
          ...c,
          total: FACILITIES_CLIENT.reduce((s, f) => s + (c.by_facility[f] || 0), 0) + (c.unattributed || 0),
        }));
        products.sort((a, b) => b.total - a.total);
        const colTotals = Object.fromEntries(
          FACILITIES_CLIENT.map(f => [f, products.reduce((s, p) => s + (p.by_facility[f] || 0), 0)])
        );
        const unattributedTotal = products.reduce((s, p) => s + (p.unattributed || 0), 0);
        const grandTotal = products.reduce((s, p) => s + p.total, 0);
        if (grandTotal === 0) {
          return (
            <Card className="mb-5" data-testid="delivered-stack-summary">
              <div className="p-3 border-b flex items-center gap-2" style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }}>
                <BarChart3 className="w-4 h-4" style={{ color: '#2B5339' }} />
                <div className="font-bold" style={{ color: '#2B5339' }}>Delivered Stack — Product × Facility</div>
              </div>
              <div className="px-3 py-2 border-b flex items-center gap-2 flex-wrap text-xs" style={{ borderColor: '#E2DFD8' }} data-testid="stack-date-filter-empty">
                <CalendarClock className="w-3.5 h-3.5" style={{ color: '#2B5339' }} />
                <span className="font-medium" style={{ color: '#2B5339' }}>Delivered between:</span>
                <Input type="date" value={stackDateFrom} onChange={(e) => setStackDateFrom(e.target.value)} className="h-7 w-36 text-xs" data-testid="stack-date-from" />
                <span style={{ color: '#5C6B61' }}>and</span>
                <Input type="date" value={stackDateTo} onChange={(e) => setStackDateTo(e.target.value)} className="h-7 w-36 text-xs" data-testid="stack-date-to" />
                <div className="flex items-center gap-1 ml-1">
                  {[
                    { k: 'today', label: 'Today' },
                    { k: 'yesterday', label: 'Yesterday' },
                    { k: 'week', label: '7d' },
                    { k: 'month', label: 'MTD' },
                    { k: 'all', label: 'All' },
                  ].map(p => (
                    <button
                      key={p.k}
                      type="button"
                      onClick={() => setStackDatePreset(p.k)}
                      className="px-2 py-0.5 border rounded hover:bg-[#FBF9F6]"
                      style={{ borderColor: '#E2DFD8', color: '#2B5339' }}
                      data-testid={`stack-preset-${p.k}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <span className="mx-2" style={{ color: '#E2DFD8' }}>|</span>
                <UserRound className="w-3.5 h-3.5" style={{ color: '#2B5339' }} />
                <span className="font-medium" style={{ color: '#2B5339' }}>Dealer:</span>
                <select
                  value={stackDealerQuery}
                  onChange={(e) => setStackDealerQuery(e.target.value)}
                  className="h-7 text-xs border rounded px-2 bg-white max-w-[240px]"
                  style={{ borderColor: '#E2DFD8', color: '#2B5339' }}
                  data-testid="stack-dealer-input"
                >
                  <option value="">All dealers</option>
                  {stackDealerOptions.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="p-6 text-center text-xs" style={{ color: '#B9BEB6' }}>
                No deliveries in selected range. Try widening the dates or click <b>All</b>.
              </div>
            </Card>
          );
        }
        const showUnattCol = anyUnattributed && unattributedTotal > 0;
        const dateLabel = (() => {
          if (!stackDateFrom && !stackDateTo) return 'All dates';
          if (stackDateFrom === stackDateTo) return stackDateFrom;
          return `${stackDateFrom || '…'} → ${stackDateTo || '…'}`;
        })();
        const exportParams = {};
        if (stackDateFrom) exportParams.date_from = stackDateFrom;
        if (stackDateTo) exportParams.date_to = stackDateTo;
        if (stackDealerQuery) exportParams.dealer_q = stackDealerQuery;
        const dateFilterBar = (
          <div className="px-3 py-2 border-b flex items-center gap-2 flex-wrap text-xs" style={{ borderColor: '#E2DFD8', background: '#FFFFFF' }} data-testid="stack-date-filter">
            <CalendarClock className="w-3.5 h-3.5" style={{ color: '#2B5339' }} />
            <span className="font-medium" style={{ color: '#2B5339' }}>Delivered between:</span>
            <Input type="date" value={stackDateFrom} onChange={(e) => setStackDateFrom(e.target.value)} className="h-7 w-36 text-xs" data-testid="stack-date-from" />
            <span style={{ color: '#5C6B61' }}>and</span>
            <Input type="date" value={stackDateTo} onChange={(e) => setStackDateTo(e.target.value)} className="h-7 w-36 text-xs" data-testid="stack-date-to" />
            <div className="flex items-center gap-1 ml-1">
              {[
                { k: 'today', label: 'Today' },
                { k: 'yesterday', label: 'Yesterday' },
                { k: 'week', label: '7d' },
                { k: 'month', label: 'MTD' },
                { k: 'all', label: 'All' },
              ].map(p => (
                <button
                  key={p.k}
                  type="button"
                  onClick={() => setStackDatePreset(p.k)}
                  className="px-2 py-0.5 border rounded hover:bg-[#FBF9F6]"
                  style={{ borderColor: '#E2DFD8', color: '#2B5339' }}
                  data-testid={`stack-preset-${p.k}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <span className="mx-2" style={{ color: '#E2DFD8' }}>|</span>
            <UserRound className="w-3.5 h-3.5" style={{ color: '#2B5339' }} />
            <span className="font-medium" style={{ color: '#2B5339' }}>Dealer:</span>
            <select
              value={stackDealerQuery}
              onChange={(e) => setStackDealerQuery(e.target.value)}
              className="h-7 text-xs border rounded px-2 bg-white max-w-[240px]"
              style={{ borderColor: '#E2DFD8', color: '#2B5339' }}
              data-testid="stack-dealer-input"
            >
              <option value="">All dealers</option>
              {stackDealerOptions.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <span className="ml-auto text-[10px]" style={{ color: '#5C6B61' }}>Showing: <b>{dateLabel}</b>{stackDealerQuery && <> · Dealer: <b>{stackDealerQuery}</b></>}</span>
          </div>
        );
        return (
          <Card className="mb-5" data-testid="delivered-stack-summary">
            <div className="p-3 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }}>
              <BarChart3 className="w-4 h-4" style={{ color: '#2B5339' }} />
              <div className="font-bold" style={{ color: '#2B5339' }}>Delivered Stack — Product × Facility</div>
              <span className="text-[11px]" style={{ color: '#5C6B61' }}>
                Total dispatched out: <b>{grandTotal.toLocaleString('en-IN')}</b> bags · click a product row to see dispatches
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await exportAPI.deliveredStackExcel(exportParams);
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `delivered_stack_${stackDateFrom || 'all'}_${stackDateTo || 'all'}.xlsx`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('Excel downloaded');
                    } catch (e) { toast.error('Failed to download Excel'); }
                  }}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 border rounded hover:bg-white"
                  style={{ borderColor: '#2B5339', color: '#2B5339' }}
                  data-testid="stack-export-excel-btn"
                >
                  <Download className="w-3 h-3" /> Excel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await exportAPI.deliveredStackPDF(exportParams);
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `delivered_stack_${stackDateFrom || 'all'}_${stackDateTo || 'all'}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('PDF downloaded');
                    } catch (e) { toast.error('Failed to download PDF'); }
                  }}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 border rounded hover:bg-white"
                  style={{ borderColor: '#C05C35', color: '#C05C35' }}
                  data-testid="stack-export-pdf-btn"
                >
                  <FileText className="w-3 h-3" /> PDF
                </button>
              </div>
            </div>
            {dateFilterBar}
            <div className="overflow-auto">
              <table className="w-full text-xs">
                <thead style={{ background: 'white' }}>
                  <tr>
                    <th className="p-2 text-left w-6"></th>
                    <th className="p-2 text-left">Product</th>
                    {FACILITIES_CLIENT.map(f => (
                      <th key={f} className="p-2 text-right">
                        <div className="inline-flex items-center gap-1 justify-end">
                          <Factory className="w-3 h-3" style={{ color: '#2B5339' }} />
                          {f}
                        </div>
                      </th>
                    ))}
                    {showUnattCol && (
                      <th className="p-2 text-right" title="Legacy delivered dispatches without facility info">Unattributed</th>
                    )}
                    <th className="p-2 text-right">Total</th>
                    <th className="p-2 text-right">Dispatches</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => {
                    const isOpen = !!expandedProducts[p.product_id];
                    return (
                      <React.Fragment key={p.product_id}>
                        <tr
                          className="border-t cursor-pointer hover:bg-[#FBF9F6]"
                          style={{ borderColor: '#E2DFD8' }}
                          onClick={() => toggleProduct(p.product_id)}
                          data-testid={`stack-product-row-${p.product_id}`}
                        >
                          <td className="p-2 text-center" style={{ color: '#5C6B61' }}>{isOpen ? '\u25BC' : '\u25B6'}</td>
                          <td className="p-2 font-medium">{p.product_name}</td>
                          {FACILITIES_CLIENT.map(f => (
                            <td key={f} className="p-2 text-right font-bold" style={{ color: '#2B5339' }} data-testid={`stack-cell-${p.product_id}-${f}`}>
                              {(p.by_facility[f] || 0).toLocaleString('en-IN')}
                            </td>
                          ))}
                          {showUnattCol && (
                            <td className="p-2 text-right" style={{ color: (p.unattributed || 0) > 0 ? '#C05C35' : '#B9BEB6' }} data-testid={`stack-cell-${p.product_id}-unatt`}>
                              {(p.unattributed || 0).toLocaleString('en-IN')}
                            </td>
                          )}
                          <td className="p-2 text-right font-bold">{p.total.toLocaleString('en-IN')}</td>
                          <td className="p-2 text-right" style={{ color: '#5C6B61' }}>{p.dispatches.length}</td>
                        </tr>
                        {isOpen && (
                          <tr style={{ background: '#FBF9F6' }} data-testid={`stack-product-detail-${p.product_id}`}>
                            <td></td>
                            <td colSpan={FACILITIES_CLIENT.length + (showUnattCol ? 4 : 3)} className="p-2">
                              <table className="w-full text-[11px]">
                                <thead>
                                  <tr style={{ color: '#5C6B61' }}>
                                    <th className="p-1.5 text-left">Dispatched</th>
                                    <th className="p-1.5 text-left">Dealer</th>
                                    {FACILITIES_CLIENT.map(f => (
                                      <th key={f} className="p-1.5 text-right">{f}</th>
                                    ))}
                                    {showUnattCol && <th className="p-1.5 text-right">Unattr.</th>}
                                    <th className="p-1.5 text-right">Total</th>
                                    <th className="p-1.5 text-left">Vehicle</th>
                                    <th className="p-1.5 text-left">Driver</th>
                                    <th className="p-1.5 text-left">Delivered</th>
                                    <th className="p-1.5 text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {p.dispatches
                                    .slice()
                                    .sort((a, b) => new Date(b.delivered_at || 0) - new Date(a.delivered_at || 0))
                                    .map(d => {
                                      const facSum = FACILITIES_CLIENT.reduce((s, f) => s + (d._per_facility[f] || 0), 0);
                                      const rowTotal = facSum + (d._unattributed || 0);
                                      return (
                                        <tr key={d.dispatch_id} className="border-t" style={{ borderColor: '#E2DFD8' }} data-testid={`stack-dispatch-${p.product_id}-${d.dispatch_id}`}>
                                          <td className="p-1.5">{fmtDateTime(d.dispatched_at)}</td>
                                          <td className="p-1.5">{d.dealer_name}</td>
                                          {FACILITIES_CLIENT.map(f => (
                                            <td key={f} className="p-1.5 text-right font-semibold" style={{ color: (d._per_facility[f] || 0) > 0 ? '#2B5339' : '#B9BEB6' }}>
                                              {d._per_facility[f] || 0}
                                            </td>
                                          ))}
                                          {showUnattCol && (
                                            <td className="p-1.5 text-right" style={{ color: (d._unattributed || 0) > 0 ? '#C05C35' : '#B9BEB6' }}>
                                              {d._unattributed || 0}
                                            </td>
                                          )}
                                          <td className="p-1.5 text-right font-bold">{rowTotal}</td>
                                          <td className="p-1.5">{d.vehicle_number || <span style={{ color: '#C05C35' }}>—</span>}</td>
                                          <td className="p-1.5">{d.driver_mobile || <span style={{ color: '#C05C35' }}>—</span>}</td>
                                          <td className="p-1.5">
                                            <span className="text-[10px] font-semibold" style={{ color: '#2B5339' }}>{fmtDateTime(d.delivered_at)}</span>
                                            {d.delivered_by && <div className="text-[9px]" style={{ color: '#5C6B61' }}>by {d.delivered_by}</div>}
                                          </td>
                                          <td className="p-1.5 text-center">
                                            <button
                                              onClick={(e) => { e.stopPropagation(); revertDelivery(d); }}
                                              disabled={markingId === d.dispatch_id}
                                              title="Revert to In Transit"
                                              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border rounded hover:bg-white"
                                              style={{ borderColor: '#C05C35', color: '#C05C35' }}
                                              data-testid={`stack-revert-${d.dispatch_id}`}
                                            >
                                              <Undo2 className="w-3 h-3" /> Revert
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#FBF9F6' }}>
                    <td></td>
                    <td className="p-2 font-bold text-right">Facility totals</td>
                    {FACILITIES_CLIENT.map(f => (
                      <td key={f} className="p-2 text-right font-bold" style={{ color: '#2B5339' }} data-testid={`stack-coltotal-${f}`}>
                        {(colTotals[f] || 0).toLocaleString('en-IN')}
                      </td>
                    ))}
                    {showUnattCol && (
                      <td className="p-2 text-right font-bold" style={{ color: '#C05C35' }} data-testid="stack-coltotal-unatt">
                        {unattributedTotal.toLocaleString('en-IN')}
                      </td>
                    )}
                    <td className="p-2 text-right font-bold" data-testid="stack-grandtotal">{grandTotal.toLocaleString('en-IN')}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        );
      })()}

      {/* Delivery facility-pick dialog */}
      <Dialog open={!!splitDlg} onOpenChange={(o) => { if (!o) setSplitDlg(null); }}>
        <DialogContent className="max-w-2xl" data-testid="delivery-split-dialog">
          <DialogHeader>
            <DialogTitle>Confirm delivery — from which facility?</DialogTitle>
          </DialogHeader>
          {splitDlg?.row && (
            <div className="space-y-3">
              <div className="text-xs" style={{ color: '#5C6B61' }}>
                Order #{(splitDlg.row.order_id || '').slice(0, 8)} · {splitDlg.row.dealer_name} · Vehicle {splitDlg.row.vehicle_number || '-'}
              </div>

              <div className="flex items-center gap-2 p-2 rounded" style={{ background: '#FBF9F6' }}>
                <span className="text-xs font-medium" style={{ color: '#5C6B61' }}>Quick fill:</span>
                <button
                  type="button"
                  onClick={() => setAllToFacility('E-302')}
                  className="text-xs px-2 py-1 rounded border hover:bg-white"
                  style={{ borderColor: '#2B5339', color: '#2B5339' }}
                  data-testid="quick-all-E-302"
                >
                  <Factory className="inline w-3 h-3 mr-1" /> All from E-302
                </button>
                <button
                  type="button"
                  onClick={() => setAllToFacility('E-303')}
                  className="text-xs px-2 py-1 rounded border hover:bg-white"
                  style={{ borderColor: '#2B5339', color: '#2B5339' }}
                  data-testid="quick-all-E-303"
                >
                  <Factory className="inline w-3 h-3 mr-1" /> All from E-303
                </button>
                <span className="text-[10px] ml-auto" style={{ color: '#5C6B61' }}>or split per product below {'\u2192'}</span>
              </div>

              <table className="w-full text-xs border">
                <thead>
                  <tr style={{ background: '#FBF9F6' }}>
                    <th className="p-2 text-left">Product</th>
                    <th className="p-2 text-right">Total</th>
                    <th className="p-2 text-right">From E-302</th>
                    <th className="p-2 text-right">From E-303</th>
                    <th className="p-2 text-right">Sum</th>
                  </tr>
                </thead>
                <tbody>
                  {(splitDlg.row.items || []).map(it => {
                    const e302 = parseInt(splitQty[it.product_id]?.['E-302'] || 0, 10) || 0;
                    const e303 = parseInt(splitQty[it.product_id]?.['E-303'] || 0, 10) || 0;
                    const sum = e302 + e303;
                    const ok = sum === it.quantity;
                    return (
                      <tr key={it.product_id} className="border-t" style={{ borderColor: '#E2DFD8' }} data-testid={`split-row-${it.product_id}`}>
                        <td className="p-2 font-medium">{it.product_name}</td>
                        <td className="p-2 text-right font-bold">{it.quantity}</td>
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            min="0"
                            max={it.quantity}
                            value={splitQty[it.product_id]?.['E-302'] ?? 0}
                            onChange={(e) => updateSplit(it.product_id, 'E-302', e.target.value, it.quantity)}
                            className="h-8 w-20 text-right ml-auto"
                            data-testid={`split-E-302-${it.product_id}`}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            min="0"
                            max={it.quantity}
                            value={splitQty[it.product_id]?.['E-303'] ?? 0}
                            onChange={(e) => updateSplit(it.product_id, 'E-303', e.target.value, it.quantity)}
                            className="h-8 w-20 text-right ml-auto"
                            data-testid={`split-E-303-${it.product_id}`}
                          />
                        </td>
                        <td className="p-2 text-right font-bold" style={{ color: ok ? '#2B5339' : '#C05C35' }}>
                          {sum}
                          {!ok && <div className="text-[10px] font-normal">need {it.quantity}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#FBF9F6' }}>
                    <td className="p-2 text-right font-bold" colSpan="2">Facility totals</td>
                    {(() => {
                      const totE302 = (splitDlg.row.items || []).reduce((s, it) => s + (parseInt(splitQty[it.product_id]?.['E-302'] || 0, 10) || 0), 0);
                      const totE303 = (splitDlg.row.items || []).reduce((s, it) => s + (parseInt(splitQty[it.product_id]?.['E-303'] || 0, 10) || 0), 0);
                      return (
                        <>
                          <td className="p-2 text-right font-bold" style={{ color: '#2B5339' }} data-testid="total-E-302">{totE302}</td>
                          <td className="p-2 text-right font-bold" style={{ color: '#2B5339' }} data-testid="total-E-303">{totE303}</td>
                          <td className="p-2 text-right font-bold">{totE302 + totE303}</td>
                        </>
                      );
                    })()}
                  </tr>
                </tfoot>
              </table>

              <div className="text-[11px] p-2 rounded" style={{ background: '#FBF9F6', color: '#5C6B61' }}>
                Stock will be deducted from the configured facility for each product when you click Confirm.
              </div>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSplitDlg(null)} disabled={!!markingId}>Cancel</Button>
            <Button onClick={confirmDelivery} disabled={!!markingId} style={{ background: '#2B5339' }} data-testid="confirm-delivery-btn">
              {markingId ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm delivery'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ PRODUCTION TAB (Sheet-based planner) ============


